import { useQuery } from '@tanstack/react-query';

import {
  type ConversationDetailsResponse,
  proposalTimelineService,
} from '@/lib/proposals/timelineService';

import { proposalQueryKeys } from '../query/keys';

/**
 * Fetch a proposal discussion conversation's detail (chains + steps) via
 * React Query so the detail panel and ActionVersionTimeline share one cached
 * result. `staleTime: 0` so every mount re-derives the current in-flight
 * chain state (required for chat rehydration; matches the fetch-on-mount
 * behavior ActionVersionTimeline already had). Disabled when there is no
 * conversation yet, so an action with no discussion issues no request.
 */
export function useDiscussionConversation(
  orgId: number,
  conversationId: number | null
) {
  return useQuery<ConversationDetailsResponse>({
    queryKey: proposalQueryKeys.discussionConversation(orgId, conversationId),
    queryFn: () => {
      if (conversationId == null) {
        throw new Error(
          'useDiscussionConversation: queryFn invoked while disabled'
        );
      }
      return proposalTimelineService.getConversationDetails(
        String(orgId),
        conversationId
      );
    },
    enabled: conversationId != null,
    // Re-derive current chain state on every mount (chat rehydration).
    staleTime: 0,
    // staleTime:0 + default refetchOnWindowFocus would refetch on every
    // focus; the live turn is already tracked via the progress poll.
    refetchOnWindowFocus: false,
    gcTime: 5 * 60_000,
  });
}
